import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Icon } from '@iconify/react';
import communityService, {
  GameDetailResponse,
  GameLineupPlayerItem,
  GameTimelineEvent,
} from '@/services/communityService';
import { Skeleton } from '@/components/shadcn/skeleton';
import { Button as ShadcnButton } from '@/components/shadcn/button';
import { historyCanGoBack } from '@/utils/nativeNavigation';
import { hostPaths } from '@/utils/hostPaths';

const normalizeTimeline = (raw: unknown): GameTimelineEvent[] => {
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (e): e is GameTimelineEvent =>
      e != null && typeof e === 'object' && 'time' in e && 'type' in e && 'team_side' in e
  ) as GameTimelineEvent[];
};

const parseTimeToSeconds = (timeStr?: string | number): number => {
  if (!timeStr) return 0;
  if (typeof timeStr === 'number') return timeStr;
  const str = String(timeStr).trim().replace(/'/g, '');
  if (str.includes(':')) {
    const parts = str.split(':').map((p) => parseInt(p, 10) || 0);
    if (parts.length === 2) return parts[0] * 60 + parts[1];
    if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  }
  const num = parseFloat(str);
  return isNaN(num) ? 0 : num * 60;
};

const getScoreFromTimeline = (timeline: GameTimelineEvent[]) => {
  let home = 0;
  let away = 0;
  timeline.forEach((e) => {
    if (e.type === 'goal' || e.type === 'own_goal') {
      if (e.team_side === 'home') home += 1;
      else if (e.team_side === 'away') away += 1;
    }
  });
  return { home, away };
};

const getStatsFromTimeline = (timeline: GameTimelineEvent[]) => {
  let goalsHome = 0;
  let goalsAway = 0;
  let assistsHome = 0;
  let assistsAway = 0;
  let savesHome = 0;
  let savesAway = 0;
  let foulsHome = 0;
  let foulsAway = 0;
  let yellowHome = 0;
  let yellowAway = 0;
  let redHome = 0;
  let redAway = 0;
  let clearanceHome = 0;
  let clearanceAway = 0;
  let keyPassHome = 0;
  let keyPassAway = 0;

  timeline.forEach((e) => {
    const isHome = e.team_side === 'home';
    const isAway = e.team_side === 'away';

    if (e.type === 'goal' || e.type === 'own_goal') {
      if (isHome) goalsHome += 1;
      else if (isAway) goalsAway += 1;
    }
    if (e.assist_id != null || e.type === 'assist') {
      if (isHome) assistsHome += 1;
      else if (isAway) assistsAway += 1;
    }
    if (e.type === 'save') {
      if (isHome) savesHome += 1;
      else if (isAway) savesAway += 1;
    }
    if (e.type === 'foul') {
      if (isHome) foulsHome += 1;
      else if (isAway) foulsAway += 1;
    }
    if (e.type === 'yellow_card' || e.detail === 'yellow_card') {
      if (isHome) yellowHome += 1;
      else if (isAway) yellowAway += 1;
    }
    if (e.type === 'red_card' || e.detail === 'red_card') {
      if (isHome) redHome += 1;
      else if (isAway) redAway += 1;
    }
    if (e.type === 'clearance') {
      if (isHome) clearanceHome += 1;
      else if (isAway) clearanceAway += 1;
    }
    if (e.type === 'key_pass') {
      if (isHome) keyPassHome += 1;
      else if (isAway) keyPassAway += 1;
    }
  });

  return {
    goalsHome,
    goalsAway,
    assistsHome,
    assistsAway,
    savesHome,
    savesAway,
    foulsHome,
    foulsAway,
    yellowHome,
    yellowAway,
    redHome,
    redAway,
    clearanceHome,
    clearanceAway,
    keyPassHome,
    keyPassAway,
  };
};

type TabId = 'timeline' | 'lineup' | 'statistik';

export default function MabarHasilPage() {
  const { gameSlug } = useParams<{ gameSlug: string }>();
  const navigate = useNavigate();
  const [game, setGame] = useState<GameDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabId>('timeline');

  useEffect(() => {
    if (!gameSlug) return;
    setLoading(true);
    communityService
      .getGameBySlug(gameSlug)
      .then(setGame)
      .catch(() => setGame(null))
      .finally(() => setLoading(false));
  }, [gameSlug]);

  const timeline = useMemo(() => normalizeTimeline(game?.timeline ?? []), [game?.timeline]);
  const timelineWithoutKickOff = useMemo(() => {
    const filtered = timeline.filter((e) => (e.type || '').toLowerCase() !== 'kick_off');
    return [...filtered].sort((a, b) => parseTimeToSeconds(b.time) - parseTimeToSeconds(a.time));
  }, [timeline]);

  const { home: homeScore, away: awayScore } = useMemo(
    () => getScoreFromTimeline(timeline),
    [timeline]
  );
  const stats = useMemo(() => getStatsFromTimeline(timeline), [timeline]);

  // Man of the Match Calculation
  const motm = useMemo(() => {
    if (!game?.lineups || game.lineups.length === 0) return null;
    const scores: Record<number, { player: GameLineupPlayerItem; goals: number; assists: number; saves: number; score: number }> = {};

    game.lineups.forEach((p) => {
      scores[p.player_participant_id] = { player: p, goals: 0, assists: 0, saves: 0, score: 0 };
    });

    timeline.forEach((e) => {
      if (e.player_id && scores[e.player_id]) {
        if (e.type === 'goal') {
          scores[e.player_id].goals += 1;
          scores[e.player_id].score += 3;
        } else if (e.type === 'save') {
          scores[e.player_id].saves += 1;
          scores[e.player_id].score += 2;
        }
      }
      if (e.assist_id && scores[e.assist_id]) {
        scores[e.assist_id].assists += 1;
        scores[e.assist_id].score += 2;
      }
    });

    const sorted = Object.values(scores).sort((a, b) => b.score - a.score);
    if (sorted.length > 0 && sorted[0].score > 0) {
      const top = sorted[0];
      let statSummary = '';
      if (top.saves > 0 && top.goals === 0) {
        statSummary = `${top.saves} penyelamatan`;
      } else {
        const parts = [];
        if (top.goals > 0) parts.push(`${top.goals} gol`);
        if (top.assists > 0) parts.push(`${top.assists} assist`);
        statSummary = parts.join(' · ') || `${top.score} poin`;
      }
      return { name: top.player.name, detail: statSummary };
    }

    // Fallback to first player if no events
    const first = game.lineups[0];
    return { name: first.name, detail: 'Pemain Terbaik' };
  }, [game?.lineups, timeline]);

  const backUrl = game?.event_slug
    ? hostPaths.mabar.analitik(game.event_slug)
    : hostPaths.mabar.list;
  const goBack = () => {
    if (historyCanGoBack()) {
      navigate(-1);
      return;
    }
    navigate(backUrl);
  };

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-6 space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-44 w-full rounded-3xl" />
        <Skeleton className="h-64 w-full rounded-3xl" />
      </div>
    );
  }

  if (!game) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-12 text-center">
        <p className="text-gray-600 font-semibold mb-4">Game tidak ditemukan</p>
        <ShadcnButton onClick={goBack} className="bg-[#FC480D] text-white font-bold rounded-full">
          Kembali ke Statistik
        </ShadcnButton>
      </div>
    );
  }

  const homeName = game.home_team_name || 'Green Team';
  const awayName = game.away_team_name || 'Navy Team';
  const homeLineup = (game.lineups ?? []).filter((l) => l.team_side === 'home');
  const awayLineup = (game.lineups ?? []).filter((l) => l.team_side === 'away');

  const resultStatus =
    homeScore === awayScore
      ? 'SERI'
      : homeScore > awayScore
      ? `${homeName.toUpperCase()} MENANG`
      : `${awayName.toUpperCase()} MENANG`;

  return (
    <div className="max-w-2xl mx-auto px-4 py-4 sm:py-6 pb-12 min-h-screen">
      {/* Mobile Topbar Header (Fixed at top for mobile) */}
      <header className="fixed top-0 left-0 right-0 z-40 bg-white border-b border-[#E0E6ED] shadow-2xs pt-safe md:hidden">
        <div className="flex items-center justify-between px-4 h-14 gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <button
              type="button"
              onClick={goBack}
              className="p-1 -ml-1 rounded-full text-[#0E1726] hover:bg-gray-100 transition-colors cursor-pointer active:scale-95 shrink-0"
              aria-label="Kembali"
            >
              <Icon icon="ph:arrow-left-bold" className="w-5 h-5" />
            </button>
            <h1 className="text-sm font-extrabold text-[#0E1726] uppercase tracking-wide truncate">
              GAME 1
            </h1>
          </div>

          <span className="px-2.5 py-0.5 rounded-full bg-[#22C55E] text-white text-[11px] font-black tracking-wide shrink-0">
            {resultStatus}
          </span>
        </div>
      </header>

      {/* Desktop Navigation Header */}
      <div className="hidden md:flex items-center justify-between mb-4 gap-3">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={goBack}
            className="w-9 h-9 rounded-xl border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 flex items-center justify-center transition-colors cursor-pointer"
            aria-label="Kembali"
          >
            <Icon icon="ph:arrow-left-bold" className="w-5 h-5" />
          </button>
          <h1 className="text-base font-bold text-[#0E1726] uppercase tracking-wide">
            GAME 1
          </h1>
        </div>

        <span className="px-3 py-1 rounded-full bg-[#22C55E] text-white text-xs font-black tracking-wide shrink-0">
          {resultStatus}
        </span>
      </div>

      {/* Dark Match Scoreboard Card */}
      <div className="bg-[#181E29] rounded-3xl p-5 sm:p-6 text-white shadow-lg mb-5 overflow-hidden">
        {/* Top Matchup Header */}
        <div className="flex items-center justify-between gap-4 py-2">
          {/* Team A */}
          <div className="flex-1 flex flex-col items-center gap-2">
            <div className="w-14 h-14 rounded-full bg-[#22C55E] text-white font-extrabold text-xl flex items-center justify-center shadow-md">
              {homeName.charAt(0).toUpperCase()}
            </div>
            <span className="text-xs sm:text-sm font-bold text-center truncate max-w-[110px]">
              {homeName}
            </span>
          </div>

          {/* Score Display */}
          <div className="flex items-center gap-3">
            <span className="text-3xl sm:text-4xl font-black">{homeScore}</span>
            <span className="text-xl font-bold text-gray-500">:</span>
            <span className="text-3xl sm:text-4xl font-black">{awayScore}</span>
          </div>

          {/* Team B */}
          <div className="flex-1 flex flex-col items-center gap-2">
            <div className="w-14 h-14 rounded-full bg-[#1E3A8A] text-white font-extrabold text-xl flex items-center justify-center shadow-md">
              {awayName.charAt(0).toUpperCase()}
            </div>
            <span className="text-xs sm:text-sm font-bold text-center truncate max-w-[110px]">
              {awayName}
            </span>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-white/10 my-4" />

        {/* Man of the Match Footer */}
        {motm && (
          <div className="flex items-center justify-between pt-1">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center shrink-0">
                <Icon icon="ph:medal-fill" className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <p className="text-[10px] uppercase font-bold tracking-wider text-gray-400">
                  MAN OF THE MATCH
                </p>
                <p className="text-xs sm:text-sm font-bold text-white">{motm.name}</p>
              </div>
            </div>

            <span className="text-xs font-semibold text-gray-300">{motm.detail}</span>
          </div>
        )}
      </div>

      {/* Main Tabbed Content Card */}
      <div className="bg-white rounded-3xl border border-[#E0E6ED] p-4 sm:p-5 shadow-2xs">
        {/* Navigation Tabs */}
        <div className="flex border-b border-gray-100 mb-5">
          {[
            { id: 'timeline' as TabId, label: 'Timeline', icon: 'ph:trend-up-bold' },
            { id: 'lineup' as TabId, label: 'Lineup', icon: 'ph:users-bold' },
            { id: 'statistik' as TabId, label: 'Statistik', icon: 'ph:chart-bar-bold' },
          ].map((t) => {
            const isActive = activeTab === t.id;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setActiveTab(t.id)}
                className={`flex-1 py-3 px-2 flex items-center justify-center gap-2 text-xs sm:text-sm transition-all cursor-pointer border-b-2 -mb-px ${
                  isActive
                    ? 'border-[#FC480D] text-[#FC480D] font-extrabold'
                    : 'border-transparent text-gray-400 font-semibold hover:text-gray-600'
                }`}
              >
                <Icon icon={t.icon} className="w-4 h-4" />
                <span>{t.label}</span>
              </button>
            );
          })}
        </div>

        {/* TAB 1: TIMELINE */}
        {activeTab === 'timeline' && (
          <div>
            {timelineWithoutKickOff.length === 0 ? (
              <p className="text-xs text-gray-400 py-8 text-center font-medium">
                Belum ada catatan kejadian dalam pertandingan ini
              </p>
            ) : (
              <div className="space-y-4">
                {timelineWithoutKickOff.map((evt, idx) => (
                  <TimelineEventCard key={idx} event={evt} game={game} />
                ))}
              </div>
            )}
          </div>
        )}

        {/* TAB 2: LINEUP */}
        {activeTab === 'lineup' && (
          <div className="space-y-6">
            {/* Team A Section */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2.5">
                  <div className="w-7 h-7 rounded-full bg-[#22C55E] text-white font-extrabold text-xs flex items-center justify-center">
                    {homeName.charAt(0).toUpperCase()}
                  </div>
                  <span className="font-bold text-sm text-[#0E1726]">{homeName}</span>
                </div>
                <span className="text-xs text-gray-400 font-semibold">{homeLineup.length} pemain</span>
              </div>

              <div className="space-y-2">
                {homeLineup.map((p, idx) => (
                  <LineupPlayerRow key={p.id || idx} player={p} teamColor="home" />
                ))}
              </div>
            </div>

            {/* Team B Section */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2.5">
                  <div className="w-7 h-7 rounded-full bg-[#1E3A8A] text-white font-extrabold text-xs flex items-center justify-center">
                    {awayName.charAt(0).toUpperCase()}
                  </div>
                  <span className="font-bold text-sm text-[#0E1726]">{awayName}</span>
                </div>
                <span className="text-xs text-gray-400 font-semibold">{awayLineup.length} pemain</span>
              </div>

              <div className="space-y-2">
                {awayLineup.map((p, idx) => (
                  <LineupPlayerRow key={p.id || idx} player={p} teamColor="away" />
                ))}
              </div>
            </div>
          </div>
        )}

        {/* TAB 3: STATISTIK */}
        {activeTab === 'statistik' && (
          <div className="space-y-4 py-2">
            <StatComparisonRow label="Gol" homeVal={stats.goalsHome} awayVal={stats.goalsAway} />
            <StatComparisonRow label="Assist" homeVal={stats.assistsHome} awayVal={stats.assistsAway} />
            <StatComparisonRow label="Penyelamatan" homeVal={stats.savesHome} awayVal={stats.savesAway} />
            <StatComparisonRow label="Key Pass" homeVal={stats.keyPassHome} awayVal={stats.keyPassAway} />
            <StatComparisonRow label="Clearance" homeVal={stats.clearanceHome} awayVal={stats.clearanceAway} />
            <StatComparisonRow label="Pelanggaran" homeVal={stats.foulsHome} awayVal={stats.foulsAway} />
            <StatComparisonRow label="Kartu kuning" homeVal={stats.yellowHome} awayVal={stats.yellowAway} />
            <StatComparisonRow label="Kartu merah" homeVal={stats.redHome} awayVal={stats.redAway} />
          </div>
        )}
      </div>
    </div>
  );
}

function TimelineEventCard({ event, game }: { event: GameTimelineEvent; game: GameDetailResponse }) {
  const isHome = event.team_side === 'home';
  const teamName = isHome ? game.home_team_name || 'Green Team' : game.away_team_name || 'Navy Team';
  const player = game.lineups.find((l) => l.player_participant_id === event.player_id || l.id === event.player_id);
  const playerName = player?.name || `#${event.player_id}`;

  const assistPlayer = event.assist_id
    ? game.lineups.find((l) => l.player_participant_id === event.assist_id || l.id === event.assist_id)
    : null;

  let eventIcon = 'ph:soccer-ball-bold';
  let eventDetail = event.type;
  let dotColor = 'bg-[#22C55E]';

  if (event.type === 'own_goal' || event.detail === 'own_goal') {
    eventIcon = 'ph:soccer-ball-bold';
    eventDetail = `Gol Bunuh Diri (OG) · Gol untuk ${teamName}`;
    dotColor = 'bg-red-500';
  } else if (event.type === 'goal') {
    eventIcon = 'ph:soccer-ball-bold';
    eventDetail = assistPlayer ? `Assist: ${assistPlayer.name}` : 'Gol';
    dotColor = 'bg-[#22C55E]';
  } else if (event.type === 'save') {
    eventIcon = 'ph:hand-palm-bold';
    eventDetail = 'Crucial Save';
    dotColor = 'bg-[#22C55E]';
  } else if (event.type === 'clearance') {
    eventIcon = 'ph:shield-check-bold';
    eventDetail = 'Clearance';
    dotColor = 'bg-[#22C55E]';
  } else if (event.type === 'key_pass') {
    eventIcon = 'ph:lightning-bold';
    eventDetail = 'Key Pass';
    dotColor = 'bg-[#22C55E]';
  } else if (event.type === 'freestyle') {
    eventIcon = 'ph:sparkles-bold';
    eventDetail = 'Skill / Freestyle';
    dotColor = 'bg-[#22C55E]';
  } else if (event.type === 'foul') {
    eventIcon = 'ph:warning-circle-bold';
    eventDetail = 'Pelanggaran';
    dotColor = 'bg-amber-500';
  } else if (event.type === 'yellow_card' || event.detail === 'yellow_card') {
    eventIcon = 'ph:rectangle-vertical-bold';
    eventDetail = 'Kartu kuning';
    dotColor = 'bg-amber-500';
  } else if (event.type === 'red_card' || event.detail === 'red_card') {
    eventIcon = 'ph:rectangle-vertical-bold';
    eventDetail = 'Kartu merah';
    dotColor = 'bg-red-500';
  }

  return (
    <div className="flex items-start gap-3 p-1">
      {/* Minute tag */}
      <span className="text-xs font-semibold text-gray-400 w-7 shrink-0 pt-0.5">
        {event.time}
      </span>

      {/* Dot indicator */}
      <span className={`w-2.5 h-2.5 rounded-full ${dotColor} shrink-0 mt-1.5`} />

      {/* Event Icon */}
      <Icon icon={eventIcon} className="w-5 h-5 text-gray-700 shrink-0 mt-0.5" />

      {/* Player & Event Info */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-bold text-[#0E1726] leading-tight truncate">{playerName}</p>
        <p className="text-xs text-gray-400 font-medium leading-tight">{eventDetail}</p>

        {/* Team Pill Badge */}
        <div className="mt-1.5">
          <span
            className={`inline-block text-[11px] font-extrabold px-2.5 py-0.5 rounded-md ${
              isHome
                ? 'bg-emerald-50 text-emerald-600'
                : 'bg-blue-50 text-blue-600'
            }`}
          >
            {teamName}
          </span>
        </div>
      </div>
    </div>
  );
}

function LineupPlayerRow({
  player,
  teamColor,
}: {
  player: GameLineupPlayerItem;
  teamColor: 'home' | 'away';
}) {
  const isHome = teamColor === 'home';
  const jerseyColor = isHome ? 'text-[#FC480D]' : 'text-[#3B82F6]';
  const jerseyNum =
    player.player_number != null &&
    player.player_number !== undefined &&
    String(player.player_number).trim() !== ''
      ? player.player_number
      : '#';
  const ratingGain = player.mmr_change ?? 0;

  return (
    <div className="p-3 rounded-2xl border border-gray-100 bg-white flex items-center justify-between gap-3 shadow-2xs">
      <div className="flex items-center gap-3 min-w-0 flex-1">
        {/* Filled Jersey Icon with Number */}
        <div className="relative w-9 h-10 flex items-center justify-center shrink-0">
          <Icon icon="ph:t-shirt-fill" className={`w-9 h-9 ${jerseyColor}`} />
          <span className="absolute inset-x-0 text-center text-[11px] font-black text-white top-2.5">
            {jerseyNum}
          </span>
        </div>

        <p className="text-xs sm:text-sm font-bold text-[#0E1726] truncate">{player.name}</p>
      </div>

      {/* Position Badge & Rating Gain Pill */}
      <div className="flex items-center gap-2 shrink-0">
        <span
          className={`px-2.5 py-0.5 rounded-full text-[11px] font-extrabold border ${
            player.role === 'GK'
              ? 'bg-amber-50 text-amber-600 border-amber-200'
              : player.role === 'DF'
              ? 'bg-blue-50 text-blue-600 border-blue-200'
              : player.role === 'MF'
              ? 'bg-emerald-50 text-emerald-600 border-emerald-200'
              : 'bg-pink-50 text-pink-600 border-pink-200'
          }`}
        >
          {player.role || 'FW'}
        </span>

        <span className="px-2.5 py-0.5 rounded-full bg-emerald-50 text-emerald-600 text-xs font-black">
          +{ratingGain}
        </span>
      </div>
    </div>
  );
}

function StatComparisonRow({
  label,
  homeVal,
  awayVal,
  isPercentage = false,
}: {
  label: string;
  homeVal: number;
  awayVal: number;
  isPercentage?: boolean;
}) {
  const total = homeVal + awayVal;
  const homePct = total > 0 ? Math.round((homeVal / total) * 100) : 50;
  const awayPct = total > 0 ? 100 - homePct : 50;

  return (
    <div className="space-y-1.5 py-1">
      <div className="flex items-center justify-between text-xs sm:text-sm font-bold">
        <span className="text-[#FC480D] w-12 text-left">{homeVal}{isPercentage ? '%' : ''}</span>
        <span className="text-gray-500 font-semibold text-center flex-1">{label}</span>
        <span className="text-[#0E1726] w-12 text-right">{awayVal}{isPercentage ? '%' : ''}</span>
      </div>
      <div className="flex h-2 rounded-full overflow-hidden bg-gray-100 gap-0.5">
        <div
          className="h-full bg-[#FC480D] rounded-l-full transition-all duration-300"
          style={{ width: `${homePct}%` }}
        />
        <div
          className="h-full bg-[#3B82F6] rounded-r-full transition-all duration-300"
          style={{ width: `${awayPct}%` }}
        />
      </div>
    </div>
  );
}
